import { z } from "zod"
import { RitualTypeEnum, RitualVisibilityEnum } from "../types"

export const ritualSchema = z
  .object({
    // Info section
    title: z.string().min(3, "Title must be at least 3 characters"),
    type: z.nativeEnum(RitualTypeEnum),
    // Ritual visibility (Public/Private/Personal)
    visibility: z.enum([
      RitualVisibilityEnum.PUBLIC,
      RitualVisibilityEnum.PRIVATE,
      RitualVisibilityEnum.PERSONAL,
    ]),
    // Access type (reflects visibility for backend)
    accessType: z.enum(["PUBLIC", "PRIVATE", "PERSONAL"]).optional(),
    dateTime: z.string().optional(),
    repeat: z.string().optional(),
    repeatAt: z.string().optional(),
    repeatAtType: z.enum(["DAYS", "WEEKS", "MONTHS", "HOURS", "DATE"]).optional(),
    duration: z.string().min(1, "Duration is required"),
    address: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    country: z.string().optional(),
    lat: z.number().optional(),
    long: z.number().optional(),
    // Allow empty string or valid URL - refine checks validity only when non-empty
    meetingLink: z
      .string()
      .refine(
        val => !val || val === "" || /^https?:\/\//.test(val),
        "Meeting link must be a valid URL starting with http:// or https://",
      )
      .optional(),
    steps: z.string().optional(),
    participationFee: z.enum(["Free", "Paid", "Donation-based"]),
    price: z.string().optional(),
    feeDetails: z.string().optional(),
    minParticipantRequirement: z.boolean().optional(),
    minParticipants: z.string().optional(),
    registrationDeadline: z.string().optional(),

    // Details section
    description: z.string().min(20, "Description must be at least 20 characters"),
    whatToBring: z.string().optional(),
    whatYoullExperience: z.string().optional(),
    intention: z.string().optional(),
    websiteOrSocialMedia: z.string().optional(),
    tags: z.array(z.string()).min(1, "At least one tag is required"),

    // Organizers (users or communities)
    organizers: z.array(
      z.object({
        id: z.string(),
        type: z.enum(["user", "community"]),
        name: z.string(),
        identifier: z.string().nullable().optional(),
        avatar: z.string().optional(),
        roleId: z.string(),
        roleName: z.string(),
        inviteNote: z.string().optional(),
      }),
    ),

    // Media
    mediaFiles: z.object({
      images: z.array(z.any()).min(1, "At least one image is required"),
      videos: z.array(z.any()).optional(),
    }),

    // Privacy
    privacySettings: z.object({
      visibility: z.array(z.string()).min(1, "Visibility setting is required"),
      comment: z.array(z.string()).min(1, "Comment setting is required"),
      edit: z.array(z.string()).min(1, "Edit setting is required"),
      join: z.array(z.string()).min(1, "Join setting is required"),
      invite: z.array(z.string()).min(1, "Invite setting is required").optional(),
      seeParticipantList: z
        .array(z.string())
        .min(1, "See participant list setting is required")
        .optional(),
    }),
  })
  .superRefine((data, ctx) => {
    // Validate custom repeat fields when repeat is "custom"
    if (data.repeat === "custom") {
      if (!data.repeatAt || data.repeatAt.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Repeat value is required for custom repeat",
          path: ["repeatAt"],
        })
      }
      if (!data.repeatAtType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Repeat type is required for custom repeat",
          path: ["repeatAtType"],
        })
      }
    }
    if (data.participationFee === "Paid") {
      if (!data.price || data.price.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Price is required for paid rituals",
          path: ["price"],
        })
      }
      if (!data.feeDetails || data.feeDetails.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Fee details are required for paid rituals",
          path: ["feeDetails"],
        })
      }
    }

    // Validate minimum participant requirement fields
    if (data.minParticipantRequirement === true) {
      if (!data.minParticipants || data.minParticipants.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Minimum number of participants is required",
          path: ["minParticipants"],
        })
      }
      if (!data.registrationDeadline || data.registrationDeadline.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Registration deadline is required",
          path: ["registrationDeadline"],
        })
      }
    }

    // Conditional validation based on ritual type
    if (
      data.type === RitualTypeEnum.VIRTUAL_COURSE &&
      (!data.meetingLink || data.meetingLink.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Meeting link is required for virtual courses",
        path: ["meetingLink"],
      })
    }

    // Date & time is required for all types except Self Guided and Self Guided Course
    if (
      data.type !== RitualTypeEnum.SELF_GUIDED &&
      data.type !== RitualTypeEnum.SELF_GUIDED_COURSE &&
      (!data.dateTime || data.dateTime.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Date & time is required",
        path: ["dateTime"],
      })
    }

    // Repeat is required for all types except Self Guided and Self Guided Course
    if (
      data.type !== RitualTypeEnum.SELF_GUIDED &&
      data.type !== RitualTypeEnum.SELF_GUIDED_COURSE &&
      (!data.repeat || data.repeat.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Repeat is required",
        path: ["repeat"],
      })
    }

    if (data.type === "SELF_GUIDED" && (!data.steps || data.steps.trim() === "")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Steps to perform are required for self-guided rituals",
        path: ["steps"],
      })
    }

    // Validate that type is a format type, not a visibility type
    // PUBLIC, PRIVATE, PERSONAL are visibility values and should not be used as type
    const visibilityTypes = ["PUBLIC", "PRIVATE", "PERSONAL"]
    if (visibilityTypes.includes(data.type)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Type must be a format (In Person, Live Online, Self Guided, or Self Guided Course), not a visibility setting",
        path: ["type"],
      })
    }
  })

export type RitualFormData = z.infer<typeof ritualSchema>
